import {
  Anchor,
  Button,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../hooks/useAuth";
import styles from "./LoginForm.module.css";

export function LoginForm() {
  const { t } = useTranslation();
  const { signInWithPassword } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isRegistrationEnabled =
    import.meta.env.VITE_ENABLE_REGISTRATION === "true";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await signInWithPassword({ email, password });
      // Navigation handled by useEffect in _public.login.lazy.tsx
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.loginFailed"));
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.wrapper}>
        {/* Left: brand panel */}
        <div className={styles.brandPanel} aria-hidden="true">
          <Text className={styles.eyebrow}>
            {t("app.description", "Distributed Maintenance Network")}
          </Text>
          <Title order={2} c="white" className={styles.brandTitle}>
            {t("app.title")}
          </Title>
          <div className={styles.divider} />
          <Text size="sm" c="dimmed" className={styles.brandTagline}>
            {t("app.tagline")}
          </Text>
        </div>

        {/* Right: form panel */}
        <div className={styles.formPanel}>
          <Title order={1} size="h3" mb="xl">
            {t("auth.signIn")}
          </Title>
          <form onSubmit={handleSubmit}>
            <Stack>
              <TextInput
                label={t("auth.email")}
                placeholder={t("auth.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
                data-testid="login-email-input"
              />
              <PasswordInput
                label={t("auth.password")}
                placeholder={t("auth.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                data-testid="login-password-input"
              />
              {error && (
                <Text c="red" size="sm" role="alert" data-testid="login-error">
                  {error}
                </Text>
              )}
              <Button
                type="submit"
                fullWidth
                mt="md"
                data-testid="login-submit"
              >
                {t("auth.signIn")}
              </Button>
            </Stack>
          </form>

          {isRegistrationEnabled && (
            <Text ta="center" mt="md" size="sm">
              {t("auth.noAccount")}{" "}
              <Anchor
                component={Link}
                to="/register"
                data-testid="login-signup-link"
              >
                {t("auth.register")}
              </Anchor>
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}
